const env = import.meta.env || {};

export const ctaLinks = {
  schedule: env.VITE_SCHEDULING_LINK || 'https://calendly.com/YOUR_LINK/20min',
  secondOpinion: '/contact',
  checkup: '#no-cost-consultation',
  guide: '/downloads/guide.pdf'
};

export const personas = [
  {
    name: 'Emerging Professionals',
    pains: ['Complex equity compensation decisions', 'Student loans and cash flow tension', 'Unsure how to prioritize saving vs. investing'],
    outcomes: [
      'Simplified roadmap for equity, savings, and benefits',
      'Clear tax guardrails for exercise and diversification',
      'Budget that funds near-term priorities and long-term goals'
    ],
    link: '/who-we-serve#emerging-professionals'
  },
  {
    name: 'Busy Families',
    pains: ['College, retirement, and lifestyle trade-offs', 'Insurance and protection blind spots', 'Desire to align giving with values'],
    outcomes: [
      'Coordinated plan that balances competing goals',
      'Right-sized coverage and estate documents',
      'Family meeting agendas that keep everyone aligned'
    ],
    link: '/who-we-serve#busy-families'
  },
  {
    name: 'Business Owners',
    pains: ['Irregular income and liquidity events', 'Evaluating retirement plans for teams', 'Need for business succession clarity'],
    outcomes: [
      'Cash management playbook tied to business cycles',
      'Tax-aware benefit and retirement plan design',
      'Succession strategies that protect enterprise value'
    ],
    link: '/who-we-serve#business-owners'
  },
  {
    name: 'Physicians & Veterans',
    pains: ['Limited time to manage finances', 'Coordinating complex benefits', 'Concern about protecting family assets'],
    outcomes: [
      'Integrated planning that supports demanding careers',
      'Benefit election guidance and risk management',
      'Coordinated investment and estate strategies'
    ],
    link: '/who-we-serve#physicians'
  },
  {
    name: 'Near-Retirees',
    pains: ['When to claim Social Security', 'Sustainable income from assets', 'Health care and Medicare planning questions'],
    outcomes: [
      'Coordinated retirement income roadmap',
      'Tax-aware withdrawal strategy with guardrails',
      'Health care and long-term care planning checklists'
    ],
    link: '/who-we-serve#near-retirees'
  },
  {
    name: 'Retirees',
    pains: ['Legacy and gifting questions', 'Market volatility anxiety', 'Coordinating with family decision-makers'],
    outcomes: [
      'Flexible spending plan anchored to goals',
      'Portfolio discipline and timely communication',
      'Legacy plan aligned with values and beneficiaries'
    ],
    link: '/who-we-serve#retirees'
  }
];

export const services = [
  {
    name: 'Financial Planning',
    description: 'Cash flow, tax strategy, college planning, and retirement projections designed to adapt as life changes.',
    expectations: ['Interactive planning sessions', 'Coordinated tax and estate touchpoints', 'Implementation support and accountability']
  },
  {
    name: 'Investment Management',
    description: 'IPS-driven portfolios aligned with risk tolerance, horizon, and tax considerations.',
    expectations: ['Custom investment policy statement', 'Ongoing rebalancing and risk reviews', 'Evidence-based portfolio construction']
  },
  {
    name: 'Advanced Wealth',
    description: 'Business planning, estate coordination, and philanthropic design for multi-generational impact.',
    expectations: ['Collaborative work with your professional team', 'Scenario modeling and what-if analysis', 'Family meeting facilitation and education']
  }
];

export const processSteps = [
  {
    label: "Discovery Meeting",
    description:
      "A guided conversation to understand your goals, values, timeline, and comfort with investment risk.",
  },
  {
    label: "Deep Dive & Data",
    description:
      "After receiving your documents, we complete a comprehensive, analytical review of what you have today and how current and historical data relate to the goals and values identified in the discovery meeting.",
  },
  {
    label: "Plan & Investments",
    description:
      "We share our findings and collaborate on a plan that gets you on track—structured to your goals and your comfort level with investments.",
  },
  {
    label: "Launch & Monitor",
    description:
      "After you approve the plan and investments, we launch and regularly monitor to help ensure performance is as expected and remains aligned with your goals and values.",
  },
  {
    label: "Course Corrections",
    description:
      "During monitoring, we’ll alert you to anything that could move you off plan. We’ll also hold meetings through the year to review progress and update your plan as needed.",
  },
];

export const philosophyPoints = [
  'Risk-first discipline grounded in understanding your capacity and willingness for volatility.',
  'Diversification across asset classes, styles, and geographies to seek resilient outcomes.',
  'Thoughtful rebalancing to keep portfolios aligned with policy targets and tax considerations.',
  'Tax-aware implementation leveraging asset location, tax-loss harvesting, and charitable planning.'
];

export const feeSchedule = {
  planning: {
    description: 'Comprehensive planning engagement with ongoing access and implementation support.',
    tiers: [
      { label: 'Launch Planning (flat)', value: 'TODO: Insert fee' },
      { label: 'Ongoing Retainer', value: 'TODO: Insert fee or range' }
    ],
    includes: [
      'Annual deep-dive planning sessions and quarterly check-ins',
      'Tax strategy coordination with your CPA or enrolled agent',
      'Secure client portal with action tracking'
    ]
  },
  aum: {
    description: 'Assets under management with trading, reporting, and coordination included.',
    tiers: [
      { label: 'First $1,000,000', value: 'TODO: Insert % or fee' },
      { label: 'Next $1,000,000', value: 'TODO: Insert % or fee' },
      { label: 'Above $2,000,000', value: 'TODO: Insert % or fee' }
    ],
    includes: [
      'Custom investment policy statement and risk budgeting',
      'Ongoing rebalancing, trading, and tax-loss harvesting',
      'Performance reporting with transparent fee breakdowns'
    ]
  },
  notes: 'All engagements include coordination with your tax and legal professionals, secure client portal access, and ongoing progress reviews.'
};

export const teamMembers = [
  {
    name: 'Jordan Carter, CFP®',
    role: 'Founder & Lead Advisor',
    bio: 'Blends corporate finance experience with a decade of advising families and founders on aligning capital with conviction.',
    credentials: 'CFP®, CKA®',
    image: '/images/team-jordan.jpg'
  },
  {
    name: 'Taylor Morgan, CPA',
    role: 'Director of Tax Strategy',
    bio: 'Specializes in proactive tax planning, equity compensation strategies, and business owner guidance.',
    credentials: 'CPA, MST',
    image: '/images/team-taylor.jpg'
  },
  {
    name: 'Avery Lee, CFP®',
    role: 'Client Experience Lead',
    bio: 'Oversees onboarding, education resources, and the client service calendar to keep households confident.',
    credentials: 'CFP®',
    image: '/images/team-avery.jpg'
  }
];

export const insightPosts = [
  {
    title: 'Charting a Confident Retirement Income Strategy',
    category: 'Planning',
    date: 'TODO: Publish date',
    description: 'How sequencing, spending guardrails, and tax-smart withdrawals can keep retirement resilient.',
    link: '#'
  },
  {
    title: 'The Case for Risk Budgets in Volatile Markets',
    category: 'Markets',
    date: 'TODO: Publish date',
    description: 'Why defining risk first empowers better portfolio decisions when markets are choppy.',
    link: '#'
  },
  {
    title: 'Business Owner Liquidity Checklist',
    category: 'Business Owners',
    date: 'TODO: Publish date',
    description: 'Key steps before and after a liquidity event to stay aligned with long-term goals.',
    link: '#'
  },
  {
    title: 'Five Tax Moves Before Year-End',
    category: 'Taxes',
    date: 'TODO: Publish date',
    description: 'Opportunities for tax-loss harvesting, Roth conversions, and charitable strategies.',
    link: '#'
  },
  {
    title: 'Estate Planning for Blended Families',
    category: 'Estate',
    date: 'TODO: Publish date',
    description: 'Considerations to balance fairness, clarity, and legacy wishes.',
    link: '#'
  },
  {
    title: 'How to Host a Family Financial Summit',
    category: 'Planning',
    date: 'TODO: Publish date',
    description: 'A guide to communicating values, decisions, and next steps with loved ones.',
    link: '#'
  }
];

export const testimonials = [
  {
    quote:
      'Carter Asset Management gave us clarity on our goals and the confidence to stay invested during volatility.',
    initials: 'L.K.',
    clientType: 'Retiree Household'
  },
  {
    quote:
      'Their planning process helped synchronize our business exit with our family priorities—without the jargon.',
    initials: 'R.S.',
    clientType: 'Entrepreneur'
  }
];

export const riskDisclosure =
  'Investing involves risk, including loss of principal. No strategy ensures success or protects against loss. Past performance is not indicative of future results.';

export const testimonialDisclosure =
  'Testimonials may not be representative of other clients and are not a guarantee of future performance. No compensation was provided. See our testimonial policy for details.';
